if (!customElements.get('aceii-world-events')) {
  customElements.define(
    'aceii-world-events',
    class AceiiWorldEvents extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-events-track]');
        this.cards = Array.from(this.querySelectorAll('[data-events-card]'));
        this.previousButton = this.querySelector('[data-events-previous]');
        this.nextButton = this.querySelector('[data-events-next]');
        this.dotsContainer = this.querySelector('[data-events-dots]');
        this.currentLabel = this.querySelector('[data-events-current]');
        this.activeIndex = 0;

        if (!this.track || this.cards.length < 2) return;

        this.buildDots();
        this.buildLoop();
        this.querySelectorAll('[data-events-image]').forEach((image) => {
          image.addEventListener('error', () => {
            image.hidden = true;
          }, { once: true });
        });
        this.previousButton?.addEventListener('click', this.showPrevious);
        this.nextButton?.addEventListener('click', this.showNext);
        this.track.addEventListener('scroll', this.handleScroll, { passive: true });
        this.addEventListener('mouseenter', this.pauseAutoplay);
        this.addEventListener('mouseleave', this.startAutoplay);
        this.addEventListener('focusin', this.pauseAutoplay);
        this.addEventListener('focusout', this.handleFocusOut);

        this.resizeObserver = new ResizeObserver(this.handleResize);
        this.resizeObserver.observe(this.track);
        requestAnimationFrame(() => {
          this.jumpTo(this.cards[0].offsetLeft);
          this.updateFromScroll();
        });
        this.startAutoplay();
      }

      disconnectedCallback() {
        this.previousButton?.removeEventListener('click', this.showPrevious);
        this.nextButton?.removeEventListener('click', this.showNext);
        this.track?.removeEventListener('scroll', this.handleScroll);
        this.removeEventListener('mouseenter', this.pauseAutoplay);
        this.removeEventListener('mouseleave', this.startAutoplay);
        this.removeEventListener('focusin', this.pauseAutoplay);
        this.removeEventListener('focusout', this.handleFocusOut);
        this.resizeObserver?.disconnect();
        this.pauseAutoplay();
        window.clearTimeout(this.scrollEndTimer);
      }

      buildDots() {
        if (!this.dotsContainer) return;

        const fragment = document.createDocumentFragment();
        this.cards.forEach((card, index) => {
          const button = document.createElement('button');
          button.className = 'aceii-world-events__dot';
          button.type = 'button';
          button.setAttribute('aria-label', `Show event ${index + 1}`);
          button.addEventListener('click', () => this.scrollToIndex(index));
          fragment.append(button);
        });
        this.dotsContainer.replaceChildren(fragment);
        this.dots = Array.from(this.dotsContainer.children);
      }

      buildLoop() {
        this.track.querySelectorAll('[data-events-clone]').forEach((clone) => clone.remove());

        const beforeFragment = document.createDocumentFragment();
        const afterFragment = document.createDocumentFragment();

        this.cards.forEach((card) => {
          const beforeClone = card.cloneNode(true);
          const afterClone = card.cloneNode(true);

          [beforeClone, afterClone].forEach((clone) => {
            clone.removeAttribute('data-events-card');
            clone.removeAttribute('id');
            clone.setAttribute('data-events-clone', '');
            clone.setAttribute('aria-hidden', 'true');
            clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
          });

          beforeFragment.append(beforeClone);
          afterFragment.append(afterClone);
        });

        this.track.prepend(beforeFragment);
        this.track.append(afterFragment);
        this.renderedCards = Array.from(this.track.children).filter((card) =>
          card.matches('[data-events-card], [data-events-clone]')
        );
      }

      showPrevious = () => {
        this.scrollByCard(-1);
      };

      showNext = () => {
        this.scrollByCard(1);
      };

      scrollByCard(direction) {
        const currentIndex = this.getClosestRenderedIndex();
        const target = this.renderedCards[currentIndex + direction];
        if (!target) return;
        this.track.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
      }

      scrollToIndex(index) {
        const card = this.cards[index];
        if (!card) return;
        this.track.scrollTo({ left: card.offsetLeft, behavior: 'smooth' });
      }

      handleScroll = () => {
        if (this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = null;
          this.updateFromScroll();
        });

        window.clearTimeout(this.scrollEndTimer);
        this.scrollEndTimer = window.setTimeout(this.normalizeLoopPosition, 180);
      };

      getClosestRenderedIndex() {
        const scrollLeft = this.track.scrollLeft;
        let closestIndex = 0;
        let closestDistance = Number.POSITIVE_INFINITY;

        this.renderedCards.forEach((card, index) => {
          const distance = Math.abs(card.offsetLeft - scrollLeft);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = index;
          }
        });

        return closestIndex;
      }

      updateFromScroll = () => {
        if (!this.track || !this.renderedCards?.length) return;

        const renderedIndex = this.getClosestRenderedIndex();
        const logicalIndex = ((renderedIndex - this.cards.length) % this.cards.length + this.cards.length) % this.cards.length;
        this.activeIndex = logicalIndex;
        this.dots?.forEach((dot, index) => {
          dot.classList.toggle('is-active', index === logicalIndex);
          dot.setAttribute('aria-current', index === logicalIndex ? 'true' : 'false');
        });

        if (this.currentLabel) {
          this.currentLabel.textContent = String(logicalIndex + 1).padStart(2, '0');
        }
      };

      normalizeLoopPosition = () => {
        const renderedIndex = this.getClosestRenderedIndex();
        const cardCount = this.cards.length;

        if (renderedIndex < cardCount) {
          this.jumpTo(this.renderedCards[renderedIndex + cardCount].offsetLeft);
        } else if (renderedIndex >= cardCount * 2) {
          this.jumpTo(this.renderedCards[renderedIndex - cardCount].offsetLeft);
        }
      };

      jumpTo(left) {
        const previousBehavior = this.track.style.scrollBehavior;
        this.track.style.scrollBehavior = 'auto';
        this.track.scrollLeft = left;
        this.track.getBoundingClientRect();
        this.track.style.scrollBehavior = previousBehavior;
      }

      handleResize = () => {
        const card = this.cards[this.activeIndex] || this.cards[0];
        if (!card) return;
        this.jumpTo(card.offsetLeft);
        this.updateFromScroll();
      };

      startAutoplay = () => {
        if (this.dataset.autoplay !== 'true' || this.cards.length < 2) return;
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

        this.pauseAutoplay();
        const delay = Number(this.dataset.autoplayDelay) || 5000;
        this.autoplayTimer = window.setInterval(this.showNext, delay);
      };

      pauseAutoplay = () => {
        window.clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      };

      handleFocusOut = (event) => {
        if (!this.contains(event.relatedTarget)) this.startAutoplay();
      };
    }
  );
}
